import { lazy, Suspense, type ComponentType } from "react";
import { Route, Switch } from "wouter";
import AppFrame from "./AppFrame";

type PageModule = { default: ComponentType<any> };
type PageKey =
  | "home" | "signal" | "vault" | "tools" | "systems" | "studio"
  | "audit" | "data" | "log" | "about" | "contact"
  | "imprint" | "privacy" | "terms" | "editorialAi" | "notFound";

const loaders: Record<PageKey, () => Promise<PageModule>> = {
  home: () => import("./pages/Home"),
  signal: () => import("./pages/Signal"),
  vault: () => import("./pages/Vault"),
  tools: () => import("./pages/Tools"),
  systems: () => import("./pages/Systems"),
  studio: () => import("./pages/Studio"),
  audit: () => import("./pages/Audit"),
  data: () => import("./pages/Data"),
  log: () => import("./pages/Log"),
  about: () => import("./pages/About"),
  contact: () => import("./pages/Contact"),
  imprint: () => import("./pages/legal/Imprint"),
  privacy: () => import("./pages/legal/Privacy"),
  terms: () => import("./pages/legal/Terms"),
  editorialAi: () => import("./pages/legal/EditorialAi"),
  notFound: () => import("./pages/NotFound"),
};

function pageKey(pathname: string): PageKey {
  if (pathname === "/") return "home";
  if (pathname === "/signal") return "signal";
  if (pathname === "/vault") return "vault";
  if (pathname === "/tools") return "tools";
  if (pathname === "/systems") return "systems";
  if (pathname === "/studio") return "studio";
  if (pathname === "/audit") return "audit";
  if (pathname === "/data") return "data";
  if (pathname === "/log" || pathname.startsWith("/log/")) return "log";
  if (pathname === "/about") return "about";
  if (pathname === "/contact") return "contact";
  if (pathname === "/legal/imprint") return "imprint";
  if (pathname === "/legal/privacy") return "privacy";
  if (pathname === "/legal/terms") return "terms";
  if (pathname === "/legal/editorial-ai") return "editorialAi";
  return "notFound";
}

/* Load the initial page before hydration so its output exactly matches the
   prerendered HTML. Every other page remains a separate, on-demand chunk. */
export async function loadClientApp(pathname: string) {
  const initialKey = pageKey(pathname);
  const initialPage = (await loaders[initialKey]()).default;
  const pages = {} as Record<PageKey, ComponentType<any>>;

  for (const key of Object.keys(loaders) as PageKey[]) {
    if (key === initialKey) {
      pages[key] = initialPage;
      continue;
    }
    const LazyPage = lazy(loaders[key]);
    pages[key] = function DeferredPage(props: any) {
      return (
        <Suspense fallback={<div className="route-loading" aria-live="polite" />}>
          <LazyPage {...props} />
        </Suspense>
      );
    };
  }

  return function ClientApp() {
    return (
      <AppFrame>
        <Switch>
          <Route path="/" component={pages.home} />
          <Route path="/signal" component={pages.signal} />
          <Route path="/vault" component={pages.vault} />
          <Route path="/tools" component={pages.tools} />
          <Route path="/systems" component={pages.systems} />
          <Route path="/studio" component={pages.studio} />
          <Route path="/audit" component={pages.audit} />
          <Route path="/data" component={pages.data} />
          <Route path="/log" component={pages.log} />
          <Route path="/log/:slug" component={pages.log} />
          <Route path="/about" component={pages.about} />
          <Route path="/contact" component={pages.contact} />
          <Route path="/legal/imprint" component={pages.imprint} />
          <Route path="/legal/privacy" component={pages.privacy} />
          <Route path="/legal/terms" component={pages.terms} />
          <Route path="/legal/editorial-ai" component={pages.editorialAi} />
          <Route component={pages.notFound} />
        </Switch>
      </AppFrame>
    );
  };
}
